import { StateCreator } from "zustand";
import {
	adjustJobStatus,
	findJobListStatus,
	getJobStage,
	RoundToType,
} from "./utils";
import { Logger } from "@/lib/logger/Logger";
import { goBackOrForwardDays } from "../../lib/utils/utils-functions";
import {
	mapFormFieldsToJobFields,
	mapFormFieldsToPatientFields,
} from "../../lib/utils/form-rule-field-mapper";
import { supabase, Tables } from "@/lib/supabase";
import {
	JobEntityType,
	JobWithShare,
	FileWithShare,
	SupabaseShareEntityViewEnum,
	SupabaseTableEnum,
	SupabaseRpcEnum,
	ClientEntityType,
	OrganizationEntityType,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { JobStatusEnum } from "@/lib/types/job";
import { useCentralStore } from "../Central";
import { showNotification } from "../Central/selectors";
import { produce } from "immer";
import { StorageBucketsEnum } from "../../types/enums";
import { PreCheckDeleteJobResult } from "../../types/delete-job.types";
import { usePatientStore } from "../Patient";
import { JobStoreUnion } from ".";
import {
	initialJobDocumentsState,
	JobDocumentWithFiles,
} from "./job-documents.store";
import { initialJobListState, JobStage } from "./job-list.store";
import { initialJobItemState } from "./job-items.store";
import { initialJobNotesState } from "./job-notes.store";
import { initialDeliveryState } from "./delivery.store";
import { initialJobFieldErrorState } from "./job-info.store";
import { fieldIdsForColumns } from "../../pages/job-configuration/field-registry";
import { v4 } from "uuid";
import { uploadFileWithProvidedPath } from "../../hooks/useStorage";
import { createFileMetadata } from "@/lib/utils/file-utils";
import { useRealtimeStore } from "@/lib/store/Realtime";
import {
	MessageType,
	JobRequestCustomV1,
	MessageContent,
} from "@/lib/store/Realtime/message.types";
import { getRulesThatAreTrueForSubmittedForm } from "../../lib/utils/form-rules-evaluator";
import { FormRule } from "../../pages/form-builder/types/ruleTypes";
import { CustomFormData } from "@/dentconnect/src/pages/job-request/types";
import { SYSTEM_STANDARD_FORM_ID } from "@/dentconnect/src/pages/job-request/constants/system-standard-form";

function isCustomFormSubmission(
	messageContent: MessageContent | null | undefined
) {
	return (
		messageContent &&
		messageContent.type === MessageType.JOB_REQUEST_CUSTOM_V1
	);
}

export interface PendingPatientDecision {
	ruleId: string;
	ruleName: string;
	jobId: number;
	matchResults: any[];
	reason: string;
	matchFieldsForRpc: any;
	patientFields: any;
	matchCount: number;
	matchType: string;
	isDifferentClient: boolean;
	isRetry?: boolean;
	constraintError?: string;
	conflictingField?: string;
}

interface JobState {
	job: JobWithShare | null;
	jobLoading: boolean;
	jobFilesLoading: boolean;
	pendingChanges: boolean;
	labJobFilesLookup: Record<string, FileWithShare>;
	medJobFilesLookup: Record<string, FileWithShare>;
	jobFilesLoadedForJobId: number | null;
	childJobChannels: Tables<"message_channels">[];
	pendingPatientDecisions: PendingPatientDecision[];
	// Local-only flag: when set, the currently-linked patient is a quick-insert (auto-created by the
	// inline autocomplete on the job page). Stays active across the session so the user can still pick
	// an existing patient via the dropdown. Cleared on reload — after reload the patient is treated
	// as confirmed and the autocomplete disappears.
	quickInsertPatientId: string | null;
}

export const initialJobState: JobState = {
	job: null,
	jobLoading: false,
	jobFilesLoading: false,
	pendingChanges: false,
	labJobFilesLookup: {},
	medJobFilesLookup: {},
	jobFilesLoadedForJobId: null,
	childJobChannels: [],
	pendingPatientDecisions: [],
	quickInsertPatientId: null,
};

export type AcceptJobRequestResult =
	| {
			success: true;
			clientId: string;
			jobId: number;
			wasAlreadyAccepted: boolean;
	  }
	| { success: false; error: string };

export interface JobSlice extends JobState {
	handleJobRouteChange: (
		jobIdParam: string,
		force?: boolean
	) => Promise<void>;
	fetchJob: (jobId: number) => Promise<JobEntityType | null>;
	createJobOutsideOfClient: (
		clientId: string
	) => Promise<JobEntityType | null>;
	createJob: (
		overrideClient?: ClientEntityType,
		initialStatus?: JobStatusEnum
	) => Promise<JobEntityType | null>;
	updateGuarantor: (guarantorId: string | null) => Promise<void>;
	deleteJob: (jobId: number) => Promise<void>;
	preCheckDeleteJob: (
		jobId: number
	) => Promise<PreCheckDeleteJobResult | null>;
	hardDeleteJob: (jobId: number, deletePatient: boolean) => Promise<boolean>;
	hardDeleteFile: (fileId: string) => Promise<boolean>;
	syncJobStatus: (jobId: number) => Promise<void>;
	updateJob: (
		field: "title" | "code" | "is_paid_by_patient",
		value: string | number | boolean,
		overrideJobId?: number,
		hideNotification?: boolean
	) => Promise<{ success: boolean; error?: string }>;
	updateJobFields: (fields: Partial<JobEntityType>) => Promise<boolean>;
	updateJobPatient: (
		patient: PatientEntityType
	) => Promise<{ success: boolean; error?: string }>;
	// Quick-insert flow
	insertQuickPatient: (
		fields: Partial<PatientEntityType>
	) => Promise<PatientEntityType | null>;
	swapToExistingPatient: (picked: PatientEntityType) => Promise<void>;
	switchPatientFromDivergence: (
		field: "first_name" | "last_name" | "code" | "birth_date",
		value: string | null
	) => Promise<void>;
	unlinkPatientFromJob: (options: {
		deletePatient: boolean;
	}) => Promise<void>;
	clearQuickInsertFlag: () => void;
	maybeRegenerateQuickInsertCode: () => Promise<void>;
	reset: () => void;
	clearCurrentJob: () => void;
	resetClientScoped: () => void;
	setPendingChanges: (pendingChanges: boolean) => void;
	pendingChangesNotification: () => void;
	shareFile: (fileId: string, jobDocumentId?: number) => Promise<void>;
	unshareFile: (fileId: string, jobDocumentId?: number) => Promise<void>;
	uploadFile: (file: File, jobId: number) => Promise<void>;
	fetchJobFiles: (jobId: number) => Promise<void>;
	getDefaultValues: (
		client: ClientEntityType,
		organization: OrganizationEntityType
	) => Promise<{
		success: boolean;
		error?: string;
		data?: {
			tpValueForJob: number;
			priceListIdForJob: string;
			tpVariationForJob: number;
			taxRateForJob: number;
			mwstNumberForJob: string;
			isMwstEustForJob: boolean;
			roundToForJob: RoundToType;
			currencyTextForJob: string;
			prodCountryChoiceForJob: string;
		};
	}>;
	acceptJobRequest: (
		channelId: string | null,
		messageContent?: JobRequestCustomV1 | null
	) => Promise<AcceptJobRequestResult>;
	denyJobRequest: (channelId: string | null) => Promise<boolean>;
	fetchChildJobChannels: (jobId: number) => Promise<void>;
	applyCustomFormRules: (
		messageContent: JobRequestCustomV1,
		newJobId: number | null,
		newJobClientId: string | null
	) => Promise<void>;
	addPendingPatientDecision: (decision: PendingPatientDecision) => void;
	resolvePatientDecision: (
		ruleId: string,
		action: "select" | "create" | "skip",
		selectedPatientId?: string,
		editedPatientData?: any
	) => Promise<void>;
	clearPendingPatientDecisions: () => void;
}

export const createJobStore: StateCreator<JobStoreUnion, [], [], JobSlice> = (
	set,
	get
) => ({
	...initialJobState,
	handleJobRouteChange: async (jobIdParam: string, force?: boolean) => {
		const jobId = Number(jobIdParam);
		const currentJobId = get().job?.id;

		if ((!jobIdParam || jobId === currentJobId) && !force) {
			return;
		}

		const { data, error } = await supabase
			.from("jobs_with_shares")
			.select(
				`*, ${SupabaseTableEnum.JOB_DOCUMENTS} (*,${SupabaseShareEntityViewEnum.FILES_WITH_SHARES}(*))`
			)
			.eq("id", jobId)
			.single();

		if (error) {
			Logger.error(error, {}, "Error fetching job");
			return;
		}

		const { job_documents, ...job } = data;

		const jobDocuments = job_documents.filter(
			(jd: JobDocumentWithFiles) => jd.deleted_at === null
		);

		set({
			...initialJobState,
			...initialJobDocumentsState,
			...initialJobNotesState,
			...initialDeliveryState,
			...(jobId !== currentJobId ? initialJobFieldErrorState : {}),
			job: job,
			jobDocuments: Array.isArray(jobDocuments)
				? jobDocuments.reduce(
						(acc: Record<number, JobDocumentWithFiles>, curr) => {
							acc[curr.id] = curr;
							return acc;
						},
						{}
					)
				: {},
		});

		const targetStatus = job.status as JobStatusEnum;
		const targetStage: JobStage | null =
			targetStatus === JobStatusEnum.IN_PROGRESS
				? getJobStage(job)
				: null;
		const { selectedJobStatus, selectedJobStage } = get();
		if (
			selectedJobStatus !== targetStatus ||
			selectedJobStage !== targetStage
		) {
			get().changeSelectedJobStatusOrStage(targetStatus, targetStage);
		}

		// Fire-and-forget secondary fetches: they each manage their own loading
		// state. Awaiting them here would block the React-Router loader and
		// delay the URL change after clicking a job in the list.
		void Promise.all([
			get().initializeJobInfoFields(job),
			get().fetchJobFiles(jobId),
			get().fetchChildJobChannels(jobId),
			get().fetchJobNotes(jobId),
			get().fetchDeliveries(jobId),
			useRealtimeStore.getState().switchChannelForJobIfExists(job),
		]).catch((err) => {
			Logger.error(err, {}, "Error in secondary job fetches");
		});
	},
	fetchJobFiles: async (jobId: number) => {
		if (!jobId) {
			Logger.error("fetchJobFiles called with null/undefined jobId");
			return;
		}

		let organizationId = useCentralStore.getState().organization?.id;

		const isRefreshOfSameJob = get().jobFilesLoadedForJobId === jobId;

		if (isRefreshOfSameJob) {
			set({ jobFilesLoading: true });
		} else {
			set({
				labJobFilesLookup: {},
				medJobFilesLookup: {},
				jobFilesLoading: true,
			});
		}

		const { data: jobFiles, error: jobFilesError } = await supabase.rpc(
			"get_files_for_job",
			{ p_job_id: jobId }
		);

		if (jobFilesError) {
			showNotification({
				message: "Fehler beim Abrufen der Dateien für den Auftrag",
				type: "error",
			});
			Logger.error(jobFilesError, {}, "Error fetching files for job");
			set({
				labJobFilesLookup: {},
				medJobFilesLookup: {},
				jobFilesLoadedForJobId: jobId,
			});
			return;
		}

		if (get().job?.id !== jobId) {
			return;
		}

		set(
			produce((state) => {
				state.labJobFilesLookup = (jobFiles ?? []).reduce(
					(
						acc: Record<string, FileWithShare>,
						row: FileWithShare
					) => {
						if (row.organization_id === organizationId) {
							acc[row.id as string] = row;
						}
						return acc;
					},
					{}
				);
				state.medJobFilesLookup = (jobFiles ?? []).reduce(
					(
						acc: Record<string, FileWithShare>,
						row: FileWithShare
					) => {
						if (row.organization_id !== organizationId) {
							acc[row.id as string] = row;
						}
						return acc;
					},
					{}
				);
				state.jobFilesLoadedForJobId = jobId;
			})
		);
	},

	setPendingChanges: (pendingChanges: boolean) => {
		set({
			pendingChanges,
		});
	},
	createJobOutsideOfClient: async (clientId: string) => {
		const clientsLookup = useCentralStore.getState().clientsLookup;
		const client = clientsLookup[clientId];

		if (!client) {
			showNotification({
				message:
					"Fehler beim Erstellen des Auftrags durch fehlende Daten.",
				type: "error",
			});
			return null;
		}

		return get().createJob(client);
	},
	createJob: async (
		overrideClient?: ClientEntityType,
		initialStatus?: JobStatusEnum
	) => {
		set({
			jobLoading: true,
		});

		const { client: currentClient, organization } =
			useCentralStore.getState();

		const client = overrideClient || currentClient;

		if (!client || !organization || !organization?.id) {
			showNotification({
				message:
					"Fehler beim Erstellen des Auftrags durch fehlende Daten.",
				type: "error",
			});
			Logger.error(
				`Unexpected error creating job. Missing client or organization data. HasClient: ${!!client}, HasOrg: ${!!organization}`
			);
			return null;
		}
		const {
			success: defaultValuesSuccess,
			data: defaultValuesData,
			error: defaultValuesError,
		} = await get().getDefaultValues(client, organization);

		if (!defaultValuesSuccess || !defaultValuesData) {
			showNotification({
				message:
					defaultValuesError ||
					"Fehler beim Abrufen der Standardwerte für den Auftrag",
				type: "error",
			});
			return null;
		}

		const today = new Date();
		const formattedToday = `${today.getFullYear()}-${String(
			today.getMonth() + 1
		).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

		const { data, error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.insert({
				code: "", // If code = '' it will be set on insert on the db
				client_id: client.id,
				title:
					// initialStatus === JobStatusEnum.PRE_REGISTERED
					// 	? "Neue Voranmeldung"
					// 	:
					"Neuer Auftrag",
				status: initialStatus ?? JobStatusEnum.IN_PROGRESS,
				organization_id: organization?.id,
				incoming_order_date: formattedToday,
				outgoing_order_date: goBackOrForwardDays(7),
				tax_rate: defaultValuesData.taxRateForJob,
				guarantor_id: client.guarantor_id,
				price_list_id: defaultValuesData.priceListIdForJob,
				tp_value: defaultValuesData.tpValueForJob,
				tp_variation: defaultValuesData.tpVariationForJob,
				mwst_number: defaultValuesData.mwstNumberForJob,
				is_mwst_eust: defaultValuesData.isMwstEustForJob,
				round_to: defaultValuesData.roundToForJob,
				currency_text: defaultValuesData.currencyTextForJob,
				prod_country_choice: defaultValuesData.prodCountryChoiceForJob,
			})
			.select()
			.single();

		if (error) {
			showNotification({
				message: "Fehler beim Erstellen des Auftrags",
				type: "error",
			});
			Logger.error(error);
			return null;
		}

		if (data) {
			Logger.info({
				message: "Auftrag erstellt",
				type: "success",
			});

			await get().initializeJobInfoFields(data);

			get().addJobToList({
				...data,
				channel_id: null,
				job_document_types: [],
			});

			set({
				jobLoading: false,
			});

			return {
				...data,
				job_documents: [],
				channel_id: null,
			};
		} else {
			showNotification({
				message: "Fehler beim Erstellen des Auftrags",
				type: "error",
			});
			return null;
		}
	},
	fetchJob: async (jobId: number) => {
		if (!jobId) return null;

		const { data, error } = await supabase
			.from("jobs_with_shares")
			.select(
				`*, ${SupabaseTableEnum.JOB_DOCUMENTS} (*,${SupabaseTableEnum.FILES}(*))`
			)
			.eq("id", jobId)
			.single();

		if (error) {
			set({
				job: null,
			});
			return null;
		}

		const { job_documents, ...job } = data;

		set({ job });

		// Initialize form fields including patient and practitioner fields
		await get().initializeJobInfoFields(job);

		await get().fetchChildJobChannels(jobId);
		return job;
	},
	deleteJob: async (jobId: number) => {
		if (!jobId) {
			Logger.error("deleteJob called with null/undefined jobId");
			return;
		}

		let job = get().job;

		// Optimistic update
		get().deleteJobFromList(jobId);
		set(
			produce((state) => {
				// If the currently selected job is deleted, set it to null
				if (state.job?.id === jobId) {
					state.job = null;
				}

				// Remove the job from pinned jobs if it is pinned
				delete state.pinnedJobs[jobId];
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.delete()
			.eq("id", jobId);

		if (error) {
			Logger.error(error, {}, "Error deleting job");
			if (job) get().addJobToList(job);
			set(
				produce((state) => {
					state.job = job;
					state.pinnedJobs[jobId] = job;
				})
			);

			showNotification({
				message: "Fehler beim Löschen des Auftrags",
				type: "error",
			});
			return;
		}
		showNotification({
			message: "Auftrag gelöscht",
			type: "success",
		});
	},

	preCheckDeleteJob: async (jobId: number) => {
		const { data, error } = await supabase.rpc("pre_check_delete_job", {
			p_job_id: jobId,
		});
		if (error) {
			Logger.error(error, {}, "Error pre-checking job deletion");
			return null;
		}
		return data as unknown as PreCheckDeleteJobResult;
	},

	hardDeleteJob: async (jobId: number, deletePatient: boolean) => {
		const { data, error } = await (supabase.rpc as any)("delete_job", {
			p_job_id: jobId,
			p_delete_patient: deletePatient,
		});
		if (error || !data?.success) {
			showNotification({
				message: data?.message ?? "Fehler beim Löschen des Auftrags",
				type: "error",
			});
			return false;
		}
		get().deleteJobFromList(jobId);
		set(
			produce((state) => {
				if (state.job?.id === jobId) state.job = null;
				delete state.pinnedJobs[jobId];
			})
		);
		showNotification({
			message: "Auftrag wurde endgültig gelöscht",
			type: "success",
		});
		return true;
	},

	hardDeleteFile: async (fileId: string) => {
		const { labJobFilesLookup } = get();
		const file = labJobFilesLookup[fileId];
		if (!file) {
			showNotification({
				message: "Datei nicht gefunden",
				type: "error",
			});
			return false;
		}

		const { data, error } = await (supabase.rpc as any)("delete_file", {
			p_file_id: fileId,
		});

		if (error || !data?.success) {
			Logger.error(error ?? data, {}, "Error deleting file");
			showNotification({
				message: data?.message ?? "Fehler beim Löschen der Datei",
				type: "error",
			});
			return false;
		}

		if (data.path_name) {
			const { error: storageError } = await supabase.storage
				.from(StorageBucketsEnum.V1)
				.remove([data.path_name]);
			if (storageError) {
				Logger.error(
					storageError,
					{},
					"Error deleting file from storage"
				);
			}
		}

		set(
			produce((state) => {
				delete state.labJobFilesLookup[fileId];
			})
		);

		showNotification({
			message: "Datei wurde endgültig gelöscht",
			type: "success",
		});
		return true;
	},

	/**
	 * updateGuarantor
	 * Allowed if: No job document has been created that has job items
	 */
	updateGuarantor: async (guarantorId: string | null) => {
		const { job } = get();
		if (!job) {
			showNotification({
				message: "Auftrag ist nicht gesetzt",
				type: "error",
			});
			return;
		}
		set(
			produce((state) => {
				state.job = {
					...state.job,
					guarantor_id: guarantorId,
				};
			})
		);

		const someJobDocumentExported = Object.values(get().jobDocuments).some(
			({ status }) =>
				!(
					status === JobStatusEnum.IN_PROGRESS ||
					status === JobStatusEnum.ARCHIVED
				)
		);
		if (someJobDocumentExported) {
			showNotification({
				message:
					"Der Garant kann nicht mehr geändert werden, da bereits ein Dokument exportiert wurde",
				type: "error",
			});
			return;
		}

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ guarantor_id: guarantorId })
			.eq("id", job.id as number);

		if (error) {
			showNotification({
				message: "Fehler beim Aktualisieren des Garanten",
				type: "error",
			});
			Logger.log(error);

			// Revert the changes
			set(
				produce((state) => {
					state.job = job;
				})
			);
			return;
		}

		get().clearFieldProblems(["jobs.guarantor_id"]);
		showNotification({
			message: "Garant aktualisiert",
			type: "success",
		});
	},

	// jobs.status is derived from the delivery note by a database trigger
	syncJobStatus: async (jobId: number) => {
		const { data, error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.select("status")
			.eq("id", jobId)
			.single();

		if (error) {
			Logger.error(error, {}, "Error fetching job status");
			return;
		}

		set(
			produce((state) => {
				const currentStatus =
					findJobListStatus(state, jobId) ??
					(state.job?.id === jobId ? state.job.status : null);
				if (currentStatus === null || currentStatus === data.status)
					return;
				adjustJobStatus(state, jobId, data.status, currentStatus);
			})
		);
	},

	// Because the patient might fail db-side (e.g. because of duplicate code),
	// we have a separate function to update the patient without optimistic update
	updateJobPatient: async (patient: PatientEntityType) => {
		const jobId: number | null = get().job?.id || null;
		if (!jobId) {
			showNotification({
				message: "Auftrag ist nicht gesetzt",
				type: "error",
			});
			return { success: false, error: "Auftrag ist nicht gesetzt" };
		}

		const patientId = patient.id;

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ patient_id: patientId })
			.eq("id", jobId);

		if (error) {
			if (error.code === "23505") {
				showNotification({
					message:
						"Der Patient kann nicht geändert werden, da für diesen Kunden bereits ein Patient mit gleichem Code existiert.",
					type: "warning",
				});
			} else {
				showNotification({
					message:
						"Fehler beim Aktualisieren des Auftrags in der Datenbank",
					type: "error",
				});
			}
			return { success: false, error: error.message };
		}
		showNotification({
			message: "Patient aktualisiert",
			type: "success",
		});
		const jobClientId = get().job?.client_id;
		if (patientId && jobClientId) {
			const patientStore = usePatientStore.getState();
			const existing = patientStore.patientsLookup[patientId];
			if (existing) {
				patientStore.upsertPatient(
					{
						...existing,
						client_id: jobClientId,
					} as PatientEntityType,
					false
				);
			}
		}
		get().initializePatientFormFields(patient);
		get().updateJobInList(jobId, "patient_id", patientId);
		set(
			produce((state) => {
				state.job["patient_id"] = patientId;
			})
		);
		get().clearPatientFieldProblems();
		get().checkInProgressJobsForPatient(patientId, jobId);
		return { success: true };
	},

	insertQuickPatient: async (fields: Partial<PatientEntityType>) => {
		const job = get().job;
		const jobId = job?.id;
		const clientId = job?.client_id;
		if (!jobId) return null;
		const patientStore = usePatientStore.getState();
		const created = await patientStore.insertQuickPatient(fields, clientId);
		if (!created || !created.id) return null;

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ patient_id: created.id })
			.eq("id", jobId);

		if (error) {
			Logger.error(error);
			showNotification({
				message:
					"Patient wurde erstellt, konnte aber dem Auftrag nicht zugeordnet werden.",
				type: "error",
			});
			await patientStore.deletePatient(created.id);
			return null;
		}

		// Do NOT overwrite formFieldsPatient from the DB-returned patient — the user may
		// have typed more characters while the INSERT was in flight, and initializing
		// from the stale snapshot would wipe those keystrokes. Just set the baseline
		// directly (for future divergence evaluation) and link the patient. The next
		// autosave tick picks up any post-INSERT edits via the existing dirty flags.
		get().updateJobInList(jobId, "patient_id", created.id);
		set(
			produce((state) => {
				state.job["patient_id"] = created.id;
				state.quickInsertPatientId = created.id;
				state.patientIdentityBaseline = {
					patientId: created.id,
					first_name: (created.first_name ?? "").trim(),
					last_name: (created.last_name ?? "").trim(),
					code: (created.code ?? "").trim(),
					birth_date: (created.birth_date ?? "").trim(),
				};
				state.patientDivergence = null;
				state.divergenceSuppressedUntil = null;
			})
		);
		get().clearPatientFieldProblems();
		return created;
	},

	swapToExistingPatient: async (picked: PatientEntityType) => {
		const jobId = get().job?.id;
		const quickInsertId = get().quickInsertPatientId;
		if (!jobId || !picked.id) return;

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ patient_id: picked.id })
			.eq("id", jobId);
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Patient konnte nicht zugeordnet werden",
				type: "error",
			});
			return;
		}

		get().initializePatientFormFields(picked);
		get().updateJobInList(jobId, "patient_id", picked.id);
		set(
			produce((state) => {
				state.job["patient_id"] = picked.id;
				state.quickInsertPatientId = null;
			})
		);
		get().clearPatientFieldProblems();

		if (quickInsertId && quickInsertId !== picked.id) {
			const patientStore = usePatientStore.getState();
			await patientStore.deletePatient(quickInsertId);
		}

		showNotification({ message: "Patient aktualisiert", type: "success" });

		get().checkInProgressJobsForPatient(picked.id, jobId);
	},

	switchPatientFromDivergence: async (_field, _value) => {
		const job = get().job;
		const jobId = job?.id;
		const currentPatientId = job?.patient_id;
		if (!jobId || !currentPatientId) return;

		// Snapshot the current form state BEFORE clearing it. It carries the user's
		// typed identity edits AND every other patient field (address, gender, etc.).
		// The autosave gate means these edits were NOT written to the DB, so the
		// current patient's row is still intact — no revert needed.
		const snapshot = { ...get().formFieldsPatient };

		const { error: unlinkErr } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ patient_id: null })
			.eq("id", jobId);
		if (unlinkErr) {
			Logger.error(unlinkErr);
			showNotification({
				message: "Auftrag konnte nicht aktualisiert werden",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.job["patient_id"] = null;
				state.quickInsertPatientId = null;
				state.formFieldsPatient = {
					title: "",
					first_name: "",
					last_name: "",
					code: "",
					birth_date: "",
					street: "",
					city: "",
					postal_code: "",
					country: "",
					additional_information: "",
					gender: "",
					ahv_number: "",
				};
				state.dirtyFieldsPatient = {};
				state.formErrors.patients = {};
				state.patientIdentityBaseline = null;
				state.patientDivergence = null;
				state.divergenceSuppressedUntil = null;
			})
		);
		get().updateJobInList(jobId, "patient_id", null);
		get().clearPatientFieldProblems();

		// Duplicate the whole patient snapshot (all fields the user has on screen),
		// blanking the code so set_patient_code regenerates it for the new row.
		const newFields: Partial<PatientEntityType> = {
			...snapshot,
			code: "",
		} as Partial<PatientEntityType>;
		await get().insertQuickPatient(newFields);
	},

	unlinkPatientFromJob: async ({ deletePatient }) => {
		const job = get().job;
		const jobId = job?.id;
		const patientId = job?.patient_id;
		const wasQuickInsert = get().quickInsertPatientId === patientId;
		if (!jobId || !patientId) return;

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ patient_id: null })
			.eq("id", jobId);
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Patient konnte nicht entfernt werden",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.job["patient_id"] = null;
				state.quickInsertPatientId = null;
				state.formFieldsPatient = {
					title: "",
					first_name: "",
					last_name: "",
					code: "",
					birth_date: "",
					street: "",
					city: "",
					postal_code: "",
					country: "",
					additional_information: "",
					gender: "",
					ahv_number: "",
				};
				state.dirtyFieldsPatient = {};
				state.formErrors.patients = {};
				state.patientIdentityBaseline = null;
				state.patientDivergence = null;
				state.divergenceSuppressedUntil = null;
			})
		);
		get().updateJobInList(jobId, "patient_id", null);
		get().clearPatientFieldProblems();

		if (deletePatient || wasQuickInsert) {
			const patientStore = usePatientStore.getState();
			await patientStore.deletePatient(patientId);
		}

		showNotification({ message: "Patient entfernt", type: "success" });
	},

	clearQuickInsertFlag: () => {
		set(
			produce((state) => {
				state.quickInsertPatientId = null;
			})
		);
	},

	maybeRegenerateQuickInsertCode: async () => {
		const job = get().job;
		const quickInsertId = get().quickInsertPatientId;
		if (!job?.patient_id || job.patient_id !== quickInsertId) return;

		const patientStore = usePatientStore.getState();
		const current = patientStore.patientsLookup[job.patient_id] as
			| PatientEntityType
			| undefined;
		if (!current || !("id" in current) || !current.id) return;

		const firstName = (current.first_name ?? "").trim();
		const lastName = (current.last_name ?? "").trim();
		const code = (current.code ?? "").trim();
		if (!firstName || !lastName || !code) return;

		// Only regenerate if the current code is a placeholder with a '0' initial
		const placeholderMatch = /^([A-Z0])([A-Z0])-(\d+)$/.exec(code);
		if (!placeholderMatch) return;
		const hasZeroInitial =
			placeholderMatch[1] === "0" || placeholderMatch[2] === "0";
		if (!hasZeroInitial) return;

		const firstInitial = firstName[0].toUpperCase();
		const lastInitial = lastName[0].toUpperCase();
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) return;

		const { count } = await supabase
			.from(SupabaseTableEnum.PATIENTS)
			.select("id", { count: "exact", head: true })
			.eq("organization_id", organizationId);

		let attempt = (count ?? 0) + 1;
		for (let i = 0; i < 10; i++) {
			const newCode = `${firstInitial}${lastInitial}-${attempt + i}`;
			const { data, error } = await supabase
				.from(SupabaseTableEnum.PATIENTS)
				.update({ code: newCode })
				.eq("id", current.id)
				.select()
				.single();
			if (!error && data) {
				patientStore.upsertPatient(data, false);
				set(
					produce((s) => {
						if (s.formFieldsPatient) {
							s.formFieldsPatient.code = newCode;
						}
						if (s.patientIdentityBaseline) {
							s.patientIdentityBaseline.code = newCode;
						}
					})
				);
				return;
			}
			if (error && error.code !== "23505") {
				Logger.error(error);
				return;
			}
		}
	},

	/*
	 * If overrideJobId is not provided, it is set to the current job id
	 * NOT USED TO UPDATE JOB STATUS
	 */
	updateJob: async (
		field: "title" | "code" | "is_paid_by_patient",
		value: string | number | boolean,
		overrideJobId?: number,
		hideNotification?: boolean
	) => {
		let previousJob: JobWithShare | null = null;
		const jobId: number | null = overrideJobId || get().job?.id || null;
		let jobStatus: JobStatusEnum | null = null;
		let jobIndex: number = -1;
		let stageIndex: JobStage | null = null;

		if (!jobId) {
			showNotification({
				message: "Auftrag ist nicht gesetzt",
				type: "error",
			});
			return { success: false, error: "Auftrag ist nicht gesetzt" };
		}

		get().updateJobInList(jobId, field, value);

		set(
			produce((state) => {
				previousJob = state.job;
				jobStatus = state.job?.status as JobStatusEnum;
				state.job[field] = value;
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ [field]: value })
			.eq("id", jobId);

		if (error) {
			// Call the showNotification before the state update
			// to avoid a previous bug where the notification did not display
			showNotification({
				message:
					"Fehler beim Aktualisieren des Auftrags in der Datenbank",
				type: "error",
			});

			set(
				produce((state) => {
					state.job = { ...previousJob };
					if (jobIndex !== -1 && jobStatus) {
						if (state.jobList[jobStatus].stages && stageIndex) {
							state.jobList[jobStatus].data[stageIndex].jobs[
								jobIndex
							] = previousJob;
						} else if (!state.jobList[jobStatus].stages) {
							state.jobList[jobStatus].data.jobs[jobIndex] =
								previousJob;
						}
					}
				})
			);
			return { success: false, error: error.message };
		} else {
			if (jobId === get().job?.id) {
				get().clearFieldProblems(fieldIdsForColumns("jobs", [field]));
			}
			if (!hideNotification) {
				showNotification({
					message: "Auftrag aktualisiert",
					type: "success",
				});
			}
		}

		return { success: true };
	},

	/**
	 * Generic update method for any job field
	 * Use this for fields other than title, code, and is_paid_by_patient, and patient_id
	 * (...which have more complex global state update logic)
	 */
	updateJobFields: async (fields: Partial<JobEntityType>) => {
		const jobId = get().job?.id;
		if (!jobId) {
			showNotification({
				message: "Auftrag ist nicht gesetzt",
				type: "error",
			});
			return false;
		}

		let previousJob: JobWithShare | null = null;

		set(
			produce((state) => {
				if (state.job) {
					previousJob = state.job;
					state.job = { ...state.job, ...fields };
				}
			})
		);

		const { error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update(fields as any)
			.eq("id", jobId);

		if (error) {
			if (previousJob) {
				set(
					produce((state) => {
						state.job = previousJob;
					})
				);
			}

			showNotification({
				message: "Fehler beim Aktualisieren des Auftrags",
				type: "error",
			});
			return false;
		}
		get().clearFieldProblems(
			fieldIdsForColumns("jobs", Object.keys(fields))
		);
		return true;
	},

	reset: () => {
		set({
			...initialJobState,
			...initialJobDocumentsState,
			...initialJobListState,
			...initialJobItemState,
			...initialJobNotesState,
		});
	},
	clearCurrentJob: () => {
		set({
			...initialJobState,
			...initialJobFieldErrorState,
			...initialJobDocumentsState,
			...initialJobNotesState,
			...initialDeliveryState,
		});
	},
	// Wipe all client-scoped job state when switching to a different client.
	// Preserves pinnedJobs (cross-client, LocalStorage-backed).
	resetClientScoped: () => {
		const pinnedJobs = get().pinnedJobs;
		set({
			...initialJobState,
			...initialJobFieldErrorState,
			...initialJobDocumentsState,
			...initialJobListState,
			...initialJobItemState,
			...initialJobNotesState,
			...initialDeliveryState,
			pinnedJobs,
		});
	},
	pendingChangesNotification: () => {
		if (get().pendingChanges) {
			showNotification({
				message:
					"Ausführung trotz ungespeicherter Auftragsinformationen.",
				type: "warning",
			});
		}
	},
	shareFile: async (fileId: string, jobDocumentId?: number) => {
		const { data: shareFileData, error } = await supabase.rpc(
			SupabaseRpcEnum.SHARE_FILE,
			{
				p_share_file_id: fileId,
				p_message_content: {
					type: "shared_file",
					file_id: fileId,
				},
			}
		);
		if (error) {
			Logger.error("Error sharing file", {}, error);
			showNotification({
				message: "Fehler beim Teilen der Datei",
				type: "error",
			});
		} else if (!(shareFileData as any)?.success) {
			showNotification({
				message: (shareFileData as any)?.message,
				type: "error",
			});
		} else {
			showNotification({
				message: (shareFileData as any)?.message,
				type: "success",
			});

			// Update file to display it as shared (only when a job is loaded)
			if (get().job?.id) {
				// Clear shared_until immediately so UI updates
				set(
					produce((state) => {
						if (state.labJobFilesLookup[fileId]) {
							state.labJobFilesLookup[fileId].shared_until = null;
						}
						if (state.medJobFilesLookup[fileId]) {
							state.medJobFilesLookup[fileId].shared_until = null;
						}
					})
				);

				if (jobDocumentId) {
					get().fetchJobDocument(jobDocumentId);
				} else {
					get().fetchJobFiles(get().job!.id!);
				}

				if (!get().job?.shared_with) {
					set(
						produce((state) => {
							if (state.job) {
								state.job.shared_with = v4();
							}
						})
					);
				}
			}
		}

		Logger.log("Shared file", {}, { shareFileData });

		// If the channel was newly created (file share was first message), switch to it
		if (
			(shareFileData as any)?.channel_created &&
			(shareFileData as any).channel_id
		) {
			useRealtimeStore
				.getState()
				.changeChannel((shareFileData as any).channel_id as string);
		}
	},
	unshareFile: async (fileId: string, jobDocumentId?: number) => {
		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.UNSHARE_FILE,
			{
				unshare_file_id: fileId,
			}
		);

		if (error) {
			showNotification({
				message: "Fehler beim Aufheben der Dateifreigabe",
				type: "error",
			});
		} else if (!(data as any)?.success) {
			showNotification({
				message: (data as any)?.message,
				type: "error",
			});
		} else {
			showNotification({
				message: "Datei nicht mehr geteilt",
				type: "success",
			});

			// Update file state (only when a job is loaded)
			if (get().job?.id) {
				set(
					produce((state) => {
						const now = new Date().toISOString();
						if (state.labJobFilesLookup[fileId]) {
							state.labJobFilesLookup[fileId].shared_until = now;
						}
						if (state.medJobFilesLookup[fileId]) {
							state.medJobFilesLookup[fileId].shared_until = now;
						}
					})
				);

				if (jobDocumentId) {
					get().fetchJobDocument(jobDocumentId);
				} else {
					get().fetchJobFiles(get().job!.id!);
				}
			}
		}
	},

	uploadFile: async (file: File, jobId: number) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			showNotification({
				message:
					"Fehler beim Hochladen der Datei für den Auftrag (Keine Organisation ausgewählt)",
				type: "error",
			});
			return;
		}

		const fileName = file.name;
		const filePath = `${v4()}/${fileName}`;

		const { success, pathName } = await uploadFileWithProvidedPath({
			fileBody: file,
			filePath: filePath,
		});

		if (!success || !pathName) {
			showNotification({
				message:
					"Fehler beim Hochladen der Datei für den Auftrag (Fehler beim Hochladen der Datei auf den Server)",
				type: "error",
			});
			Logger.error("Error uploading file for job");
			return;
		}
		const { error, data } = await supabase
			.from(SupabaseTableEnum.FILES)
			.insert([
				{
					file_name: fileName,
					bucket_name: StorageBucketsEnum.V1,
					path_name: pathName,
					job_id: jobId,
					organization_id: organizationId,
					meta_data: createFileMetadata(file),
				},
			])
			.select()
			.single();

		if (error) {
			showNotification({
				message:
					"Fehler beim Hochladen der Datei für den Auftrag (Fehler beim Speichern der Datei in der Datenbank)",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.labJobFilesLookup[data.id] = data;
			})
		);
	},
	getDefaultValues: async (
		client: ClientEntityType,
		organization: OrganizationEntityType
	) => {
		if (!client?.id || !organization?.id) {
			return {
				success: false,
				error: "Fehler beim Abrufen der Standardwerte für den Auftrag (Fehlende Daten)",
			};
		}

		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.GET_JOB_DEFAULT_VALUES,
			{
				p_client_id: client.id,
				p_organization_id: organization.id,
			}
		);

		if (error || !(data as any)?.success) {
			return {
				success: false,
				error: error ? error.message : (data as any)?.message,
			};
		}

		return {
			success: true,
			data: {
				tpValueForJob: (data as any)?.tp_value as number,
				priceListIdForJob: (data as any)?.price_list_id as string,
				tpVariationForJob: (data as any)?.tp_variation as number,
				taxRateForJob: (data as any)?.tax_rate as number,
				mwstNumberForJob: (data as any)?.mwst_number as string,
				isMwstEustForJob: (data as any)?.is_mwst_eust as boolean,
				roundToForJob: (data as any)?.round_to as RoundToType,
				currencyTextForJob: (data as any)?.currency_text as string,
				prodCountryChoiceForJob: (data as any)?.prod_country_choice as string,
			},
		};
	},
	acceptJobRequest: async (
		channelId: string | null,
		messageContent?: JobRequestCustomV1 | null
	) => {
		if (!channelId) {
			showNotification({
				message: "Fehler beim Annehmen des Auftrags",
				type: "error",
			});
			return {
				success: false,
				error: "Fehler beim Annehmen des Auftrags (Keine Channel-ID)",
			};
		}

		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.ACCEPT_JOB_REQUEST,
			{
				p_channel_id: channelId,
			}
		);

		if (error) {
			showNotification({
				message: "Fehler beim Annehmen des Auftrags",
				type: "error",
				description: error.message,
			});
			Logger.error(error, {}, "Error accepting job request");
			return { success: false, error: error.message };
		}

		if (data && !(data as any)?.success) {
			showNotification({
				message:
					(data as any)?.message ||
					"Fehler beim Annehmen des Auftrags",
				type: "error",
			});
			return {
				success: false,
				error: (data as any)?.message || "Unknown error",
			};
		}

		// Check if job was already accepted (idempotency - handles realtime failures)
		const wasAlreadyAccepted = (data as any)?.already_accepted;

		// The RPC has created a new job (or returned existing job)
		const newJob = (data as any)?.job;
		if (newJob) {
			set({
				job: newJob,
			});

			// TODO: This is a hack to refresh the channels, but it's not the best solution
			// because it fetches all channels, not just the one that is relevant to the job
			useRealtimeStore.getState().fetchLatestChannels();

			get().addJobToList({
				...newJob,
				channel_id: channelId,
				job_document_types: [],
			});

			// If the job was just accepted, it now has the status IN_PROGRESS
			get().changeSelectedJobStatusOrStage(
				JobStatusEnum.IN_PROGRESS,
				"empty"
			);
			set(
				produce((state) => {
					state.jobRequestChannels = state.jobRequestChannels.filter(
						(c: { channelId: string }) => c.channelId !== channelId
					);
				})
			);

			// Only apply rules if job is being accepted for the first time
			if (!wasAlreadyAccepted && isCustomFormSubmission(messageContent)) {
				if (!messageContent) {
					Logger.error("Message content is null or undefined");
					showNotification({
						message:
							"Fehler beim Ausführen der Regeln für den Auftrag (Fehlende Nachrichtendaten)",
						type: "error",
					});
					return {
						success: false,
						error: "Fehler beim Ausführen der Regeln für den Auftrag (Fehlende Nachrichtendaten)",
					};
				}
				await get().applyCustomFormRules(
					messageContent,
					newJob.id,
					newJob.client_id
				);
			}

			return {
				success: true,
				clientId: newJob.client_id,
				jobId: newJob.id,
				wasAlreadyAccepted,
			};
		} else {
			showNotification({
				message: "Fehler beim Annehmen des Auftrags",
				type: "error",
				description: "Kein Auftrag zurückgegeben",
			});
			return {
				success: false,
				error: "Fehler beim Annehmen des Auftrags (Kein Job zurückgegeben)",
			};
		}
	},
	denyJobRequest: async (channelId: string | null) => {
		if (!channelId) {
			showNotification({
				message: "Fehler beim Ablehnen des Auftrags",
				type: "error",
			});
			return false;
		}

		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.DENY_JOB_REQUEST,
			{
				p_channel_id: channelId,
			}
		);

		if (error || !(data as any)?.success) {
			showNotification({
				message: "Fehler beim Ablehnen des Auftrags",
				type: "error",
				description: error ? error.message : (data as any)?.message,
			});
			Logger.error(error, {}, "Error denying job request");
			return false;
		}

		// We don't show a success message here because a message will be sent in the opened channel

		return true;
	},
	fetchChildJobChannels: async (jobId: number) => {
		if (!jobId) {
			Logger.error(
				"fetchChildJobChannels called with null/undefined jobId"
			);
			return;
		}

		const { data, error } = await supabase
			.from("message_channels")
			.select("*")
			.eq("parent_job_id", jobId);

		if (error) {
			Logger.error(error, {}, "Error fetching child jobs");
			showNotification({
				message: "Fehler beim Abrufen der Aufträge",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.childJobChannels = data;
			})
		);
	},
	applyCustomFormRules: async (
		messageContent: JobRequestCustomV1,
		newJobId: number | null,
		newJobClientId: string | null
	) => {
		if (!newJobId) {
			showNotification({
				message:
					"Fehler beim Ausführen der Regeln für den Auftrag (Fehlende Auftrags-ID)",
				type: "error",
			});
			return;
		}

		if (!newJobClientId) {
			showNotification({
				message:
					"Fehler beim Ausführen der Regeln für den Auftrag (Fehlende Auftrags-Kunden-ID)",
				type: "error",
			});
			return;
		}

		if (messageContent.customFormId === SYSTEM_STANDARD_FORM_ID) {
			return;
		}

		// Currently, we don't have a forms store, so we fetch the rules of the submitted form from the database
		const { data: formConfigData, error: formError } = await supabase
			.from(SupabaseTableEnum.FORMS)
			.select("rules")
			.eq("id", messageContent.customFormId)
			.single();

		if (formError || !formConfigData) {
			Logger.error(formError, {}, "Error fetching form data");
			showNotification({
				message:
					"Fehler beim Ausführen der Regeln für den Auftrag (Fehler beim Abrufen der Formulardaten)",
				type: "error",
			});
			return;
		}

		if (!formConfigData.rules || !Array.isArray(formConfigData.rules)) {
			Logger.warn(
				`No rules found for form with id ${messageContent.customFormId}`
			);
			return;
		}

		const formRules = formConfigData.rules as unknown as FormRule[];
		const submittedFormData =
			messageContent.customFormData as CustomFormData;

		// Evaluate rules against the form data
		const rulesThatAreMet = getRulesThatAreTrueForSubmittedForm(
			formRules,
			submittedFormData
		);

		const executionLog: {
			ruleName: string;
			attemptedAction: string;
			success: boolean;
			successMessage: string;
			errorMessage: string;
			additionalInfo: any;
		}[] = [];

		// Execute actions for matching rules
		for (const metRule of rulesThatAreMet) {
			const ruleAction = metRule.action;
			const ruleActionType = ruleAction.type;

			if (ruleActionType === "create_job_document") {
				let createdJobDocumentId: number | undefined;

				if (ruleAction.templateId === null) {
					createdJobDocumentId = await get().createJobDocument(
						ruleAction.documentType
					);
				} else if (ruleAction.templateId && ruleAction.templateId > 0) {
					createdJobDocumentId =
						await get().createJobDocumentFromTemplate(
							ruleAction.templateId,
							ruleAction.documentType
						);
				} else {
					Logger.error(
						`Invalid templateId: ${ruleAction.templateId}`
					);
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage: `Ungültige Vorlagen-ID: ${ruleAction.templateId}`,
						additionalInfo: {
							templateId: ruleAction.templateId,
							documentType: ruleAction.documentType,
						},
					});
					continue;
				}

				if (!createdJobDocumentId) {
					Logger.error("Error creating job document");
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage:
							ruleAction.templateId === null
								? "Fehler beim Erstellen des Dokuments"
								: "Fehler beim Erstellen des Dokuments aus Vorlage",
						additionalInfo: {
							templateId: ruleAction.templateId,
							documentType: ruleAction.documentType,
						},
					});
				} else {
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: true,
						successMessage: "Auftragsdokument automatisch erstellt",
						errorMessage: "",
						additionalInfo: {
							templateId: ruleAction.templateId,
							documentType: ruleAction.documentType,
						},
					});
				}
			} else if (ruleActionType === "prefill_job_fields") {
				const fieldsToUpdate = mapFormFieldsToJobFields(
					ruleAction.fieldMappings,
					submittedFormData
				);

				// Update job fields if any mappings were valid
				if (Object.keys(fieldsToUpdate).length > 0) {
					const success = await get().updateJobFields(fieldsToUpdate);
					if (success) {
						executionLog.push({
							ruleName: metRule.name,
							attemptedAction: ruleActionType,
							success: true,
							successMessage:
								"Auftragsfelder automatisch vorausgefüllt",
							errorMessage: "",
							additionalInfo: {
								fieldsToUpdate,
							},
						});
					} else {
						executionLog.push({
							ruleName: metRule.name,
							attemptedAction: ruleActionType,
							success: false,
							successMessage: "",
							errorMessage:
								"Fehler beim Vorausfüllen der Auftragsfelder",
							additionalInfo: {
								fieldsToUpdate,
							},
						});
					}
				} else {
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage:
							"Auftragsfelder konnten nicht automatisch vorausgefüllt werden",
						additionalInfo: {
							fieldsToUpdate,
						},
					});
				}
			} else if (ruleActionType === "create_patient") {
				const matchCriteria: any = {};
				const matchCriteriaMissing = [];
				const organizationId =
					useCentralStore.getState().organization?.id;
				if (organizationId) {
					matchCriteria.organization_id = organizationId;
				} else {
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage: "Fehlende Organisation",
						additionalInfo: {
							matchCriteria,
						},
					});
					continue;
				}

				// Since patients are unique per client, we also add client_id to the match criteria
				matchCriteria.client_id = newJobClientId;

				// go through every field that needs to be matched to select and existing patient
				for (const fieldKey of ruleAction.matchFields) {
					// fieldKey has shape patients.first_name; fieldName has shape first_name
					const nameOfFieldToMatch = fieldKey.split(".")[1];
					const mappingOfFieldToMatch = ruleAction.fieldMappings.find(
						(m) => m.patientFieldKey === fieldKey
					);

					const mappingIsToFormField =
						mappingOfFieldToMatch?.valueSource === "form_field" &&
						mappingOfFieldToMatch.formFieldId;
					let valueThatMappingResolvesTo = mappingIsToFormField
						? messageContent.customFormData[
								mappingOfFieldToMatch.formFieldId as string
							]?.fieldValue
						: mappingOfFieldToMatch?.customValue;

					// Trim string values to avoid whitespace issues in matching
					if (typeof valueThatMappingResolvesTo === "string") {
						valueThatMappingResolvesTo =
							valueThatMappingResolvesTo.trim();
					}

					// Only add non-empty values to matchCriteria
					// Empty/null values are allowed for optional fields like last_name
					// The RPC will treat missing fields as NULL and match accordingly
					if (
						valueThatMappingResolvesTo !== undefined &&
						valueThatMappingResolvesTo !== null &&
						valueThatMappingResolvesTo !== ""
					) {
						matchCriteria[nameOfFieldToMatch] =
							valueThatMappingResolvesTo;
					}
				}

				if (Object.keys(matchCriteria).length === 0) {
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage: "Keine Felder zum Zuweisen ausgewählt",
						additionalInfo: {
							matchCriteria,
						},
					});
					continue;
				}

				Logger.info(
					"Matching and assigning patient with matchCriteria",
					{ matchCriteria }
				);

				// Build match fields for RPC
				const matchFieldsForRpc: any = {};
				for (const fieldKey of ruleAction.matchFields) {
					const fieldName = fieldKey.split(".")[1];
					const value = matchCriteria[fieldName];
					if (value !== undefined && value !== null) {
						matchFieldsForRpc[fieldName] = value;
					}
				}

				// Get all patient data for creation if needed
				const patientFields = mapFormFieldsToPatientFields(
					ruleAction.fieldMappings,
					messageContent.customFormData
				);

				if (
					Object.keys(patientFields).length === 0 &&
					Object.keys(matchFieldsForRpc).length === 0
				) {
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage:
							"Keine Felder zum Zuweisen oder Erstellen ausgewählt",
						additionalInfo: {
							patientFields,
							matchCriteria,
						},
					});
					continue;
				}

				const { data: rpcResult, error: rpcError } =
					(await supabase.rpc(
						SupabaseRpcEnum.MATCH_AND_ASSIGN_PATIENT,
						{
							p_job_id: newJobId,
							p_organization_id: organizationId,
							p_client_id: newJobClientId,
							p_match_fields: matchFieldsForRpc,
							p_patient_data: patientFields,
						}
					)) as { data: any; error: any };

				Logger.info("Patient match and assign result", {
					rpcResult,
					rpcError,
				});

				if (rpcError) {
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage: `Fehler beim Verarbeiten des Patienten: ${rpcError.message}`,
						additionalInfo: {
							rpcError,
							matchCriteria,
						},
					});
					continue;
				}

				// Handle RPC result
				if (rpcResult && rpcResult.success) {
					// STANDARD CASE: Patient matched/created and assigned successfully
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: true,
						successMessage:
							rpcResult.message ||
							"Patient erfolgreich zugewiesen",
						errorMessage: "",
						additionalInfo: {
							patientId: rpcResult.patient_id,
							action: rpcResult.action,
						},
					});
				} else if (
					rpcResult &&
					rpcResult.reason === "constraint_violation"
				) {
					// EDGE CASE: Constraint violation during patient creation
					const decisionId = `${metRule.id}-${Date.now()}`;

					get().addPendingPatientDecision({
						ruleId: decisionId,
						ruleName: metRule.name,
						jobId: newJobId,
						matchResults: [],
						reason: "constraint_violation",
						matchFieldsForRpc,
						patientFields:
							rpcResult.attempted_data || patientFields,
						matchCount: 0,
						matchType: "",
						isDifferentClient: false,
						isRetry: false,
						constraintError: rpcResult.message,
						conflictingField: rpcResult.conflicting_field,
					});

					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage:
							rpcResult.message ||
							"Eindeutigkeitsbeschränkung verletzt",
						additionalInfo: {
							reason: "constraint_violation",
							conflictingField: rpcResult.conflicting_field,
							requiresUserInput: true,
							decisionId,
						},
					});
				} else if (rpcResult) {
					// EDGE CASE: Other cases requiring user input (multiple matches, pattern matches, etc.)
					const decisionId = `${metRule.id}-${Date.now()}`;

					get().addPendingPatientDecision({
						ruleId: decisionId,
						ruleName: metRule.name,
						jobId: newJobId,
						matchResults: rpcResult.match_results || [],
						reason: rpcResult.reason,
						matchFieldsForRpc,
						patientFields,
						matchCount: rpcResult.match_count || 0,
						matchType: rpcResult.match_type || "unknown",
						isDifferentClient:
							rpcResult.is_different_client || false,
					});

					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage:
							rpcResult.message ||
							"Manuelle Auswahl erforderlich",
						additionalInfo: {
							reason: rpcResult.reason,
							matchResults: rpcResult.match_results,
							matchCount: rpcResult.match_count,
							matchType: rpcResult.match_type,
							isDifferentClient: rpcResult.is_different_client,
							requiresUserInput: true,
							decisionId,
						},
					});
				} else {
					executionLog.push({
						ruleName: metRule.name,
						attemptedAction: ruleActionType,
						success: false,
						successMessage: "",
						errorMessage:
							"Unerwarteter Fehler beim Verarbeiten des Patienten",
						additionalInfo: {
							rpcResult,
							matchCriteria,
						},
					});
				}
			} else {
				executionLog.push({
					ruleName: metRule.name,
					attemptedAction: ruleActionType,
					success: false,
					successMessage: "",
					errorMessage: "Diese Aktion wird nicht unterstützt",
					additionalInfo: {
						ruleAction,
					},
				});
			}
		}

		// Show consolidated notification for rule execution
		const successfulRules = executionLog.filter((log) => log.success);
		const failedRules = executionLog.filter((log) => !log.success);

		if (executionLog.length > 0) {
			if (successfulRules.length > 0 && failedRules.length === 0) {
				// All rules succeeded
				const ruleNames = successfulRules
					.map((r) => r.ruleName)
					.filter((name) => name)
					.join(", ");
				showNotification({
					message: ruleNames
						? `Regeln erfolgreich ausgeführt: ${ruleNames}`
						: "Regeln erfolgreich ausgeführt",
					type: "success",
				});
			} else if (successfulRules.length > 0 && failedRules.length > 0) {
				// Mixed results
				const successfulRuleNames = successfulRules
					.map((r) => r.ruleName)
					.filter((name) => name)
					.join(", ");
				const failedRuleNames = failedRules
					.map((r) => r.ruleName)
					.filter((name) => name)
					.join(", ");
				showNotification({
					message: successfulRuleNames
						? `Regeln erfolgreich ausgeführt: ${successfulRuleNames}`
						: `${successfulRules.length} ${
									successfulRules.length === 1
										? "Regel"
										: "Regeln"
							  } erfolgreich ausgeführt` +
							  "\n" +
							  failedRuleNames
							? `Regeln fehlgeschlagen: ${failedRuleNames}`
							: `${failedRules.length} ${
									failedRules.length === 1
										? "Regel"
										: "Regeln"
								} fehlgeschlagen`,
					type: "warning",
				});
			} else if (failedRules.length > 0) {
				// All rules failed
				const failedRuleNames = failedRules
					.map((r) => r.ruleName)
					.filter((name) => name)
					.join(", ");
				showNotification({
					message: failedRuleNames
						? `Fehler beim Ausführen der Regeln: ${failedRuleNames}.  Gründe dafür: ${failedRules
								.map((r) => r.errorMessage)
								.filter((message) => message)
								.join(", ")}`
						: `${failedRules.length} ${
								failedRules.length === 1 ? "Regel" : "Regeln"
							} fehlgeschlagen.  Gründe dafür: ${failedRules
								.map((r) => r.errorMessage)
								.filter((message) => message)
								.join(", ")}`,
					type: "error",
				});
			}
		}

		Logger.info("Execution log", executionLog);

		// Refetch job and patients to ensure global state is up to date
		if (successfulRules.length > 0) {
			await get().fetchJob(newJobId);
			await usePatientStore.getState().fetchPatients(newJobClientId);
		}
	},
	addPendingPatientDecision: (decision: PendingPatientDecision) => {
		set(
			produce((state) => {
				state.pendingPatientDecisions.push(decision);
			})
		);
	},
	resolvePatientDecision: async (
		ruleId: string,
		action: "select" | "create" | "skip",
		selectedPatientId?: string,
		editedPatientData?: any
	) => {
		const decision = get().pendingPatientDecisions.find(
			(d) => d.ruleId === ruleId
		);
		if (!decision) {
			Logger.error("Pending patient decision not found", {}, { ruleId });
			return;
		}

		// Remove from pending
		set(
			produce((state) => {
				state.pendingPatientDecisions =
					state.pendingPatientDecisions.filter(
						(d: PendingPatientDecision) => d.ruleId !== ruleId
					);
			})
		);

		if (action === "skip") {
			Logger.info("User skipped patient decision", { ruleId, decision });
			return;
		}

		if (action === "select" && selectedPatientId) {
			// Assign selected patient to job
			const { error } = await supabase
				.from(SupabaseTableEnum.JOBS)
				.update({ patient_id: selectedPatientId })
				.eq("id", decision.jobId);

			if (error) {
				showNotification({
					message: `Fehler beim Zuweisen des Patienten: ${error.message}`,
					type: "error",
				});
				Logger.error(error, {}, "Error assigning selected patient");
			} else {
				showNotification({
					message: "Patient erfolgreich zugewiesen",
					type: "success",
				});
				// Refresh job if it's the current job
				if (get().job?.id === decision.jobId) {
					await get().fetchJob(decision.jobId);
				}

				// Refetch patients to ensure global state is up to date
				const { data: jobData } = await supabase
					.from(SupabaseTableEnum.JOBS)
					.select("client_id")
					.eq("id", decision.jobId)
					.single();
				if (jobData?.client_id) {
					await usePatientStore
						.getState()
						.fetchPatients(jobData.client_id);
				}
			}
		} else if (action === "create") {
			// Create new patient and assign using RPC
			const organizationId = useCentralStore.getState().organization?.id;
			if (!organizationId) {
				showNotification({
					message: "Fehlende Organisation",
					type: "error",
				});
				return;
			}

			// Get client_id for the job
			const { data: jobData } = await supabase
				.from(SupabaseTableEnum.JOBS)
				.select("client_id")
				.eq("id", decision.jobId)
				.single();

			if (!jobData?.client_id) {
				showNotification({
					message: "Fehler: Kunde nicht gefunden",
					type: "error",
				});
				return;
			}

			// Call RPC to create patient
			const { data: rpcResult, error: rpcError } = (await supabase.rpc(
				SupabaseRpcEnum.MATCH_AND_ASSIGN_PATIENT as any,
				{
					p_job_id: decision.jobId,
					p_organization_id: organizationId,
					p_client_id: jobData.client_id,
					p_match_fields: {}, // Empty match fields since we're forcing creation
					p_patient_data: editedPatientData ?? decision.patientFields,
				}
			)) as { data: any; error: any };

			if (rpcError) {
				showNotification({
					message: `Fehler beim Erstellen des Patienten: ${rpcError.message}`,
					type: "error",
				});
				Logger.error(rpcError, {}, "Error creating patient via RPC");
				return;
			}

			if (rpcResult && rpcResult.success) {
				showNotification({
					message:
						rpcResult.message ||
						"Neuer Patient erstellt und zugewiesen",
					type: "success",
				});
				// Refresh job if it's the current job
				if (get().job?.id === decision.jobId) {
					await get().fetchJob(decision.jobId);
				}

				// Refetch patients to ensure global state is up to date
				if (rpcResult?.patient_id) {
					await usePatientStore
						.getState()
						.fetchPatientsByIds([rpcResult.patient_id]);
				}
			} else if (rpcResult && !rpcResult.success) {
				// Any failure - reopen dialog for user to resolve
				const retryDecisionId = `${
					decision.ruleId
				}-retry-${Date.now()}`;

				if (rpcResult.reason === "constraint_violation") {
					// Constraint violation - show edit form
					get().addPendingPatientDecision({
						ruleId: retryDecisionId,
						ruleName: decision.ruleName,
						jobId: decision.jobId,
						matchResults: [],
						reason: "constraint_violation",
						matchFieldsForRpc: decision.matchFieldsForRpc,
						patientFields:
							rpcResult.attempted_data || decision.patientFields,
						matchCount: 0,
						matchType: "",
						isDifferentClient: false,
						isRetry: true,
						constraintError: rpcResult.message,
						conflictingField: rpcResult.conflicting_field,
					});

					Logger.info(
						"Constraint violation during patient creation - prompting user to fix",
						{
							rpcResult,
							decision,
						}
					);
				} else {
					// Other failures (multiple_matches, pattern_match, etc.) - show selection dialog
					get().addPendingPatientDecision({
						ruleId: retryDecisionId,
						ruleName: decision.ruleName,
						jobId: decision.jobId,
						matchResults: rpcResult.match_results || [],
						reason: rpcResult.reason,
						matchFieldsForRpc: decision.matchFieldsForRpc,
						patientFields: decision.patientFields,
						matchCount: rpcResult.match_count || 0,
						matchType: rpcResult.match_type || "unknown",
						isDifferentClient:
							rpcResult.is_different_client || false,
						isRetry: true,
					});

					Logger.info("Patient creation failed - reopening dialog", {
						rpcResult,
						decision,
					});
				}
			} else if (rpcResult) {
				// Unexpected response format
				showNotification({
					message:
						rpcResult?.message ||
						"Fehler beim Erstellen des Patienten",
					type: "error",
				});
			}
		}
	},
	clearPendingPatientDecisions: () => {
		set(
			produce((state) => {
				state.pendingPatientDecisions = [];
			})
		);
	},
});
